import type { CapabilityConfig, Message } from '../../types';
import {
  hasOnlyKeysFromSet,
  isValidMessageConfig,
  hasValue,
} from '../../utils';
import type * as AppsDockControl from './appsDockControl';
import type * as SidebarControl from './sidebarMenuControl';

export type Control = {
  id: string;
  instanceId: string;
  label: string;
  icon?: string;
  onClick: () => void;
};

export type Constraints =
  | AppsDockControl.Constraints
  | SidebarControl.Constraints;

const VALID_CONFIG_KEYS = ['type', 'icon', 'label', 'messages'] as const;

export type Config = CapabilityConfig & {
  label: string;
  icon?: string; // At some point, we should make this required for this capability
  messages: Message[];
};

/**
 * Validate the shared control config shape.
 * @param additionalValidKeys - Extra config keys a specific control capability
 * accepts on top of the base control keys (e.g. `featureFlag` for
 * sidebar-menu-control). The caller is responsible for validating the values
 * of those extra keys.
 */
export function isValidConfig(
  config: CapabilityConfig,
  additionalValidKeys: readonly string[] = [],
): config is Config {
  if (
    !hasOnlyKeysFromSet(config, [...VALID_CONFIG_KEYS, ...additionalValidKeys])
  ) {
    return false;
  }

  if (
    typeof config.type !== 'string' ||
    typeof config['label'] !== 'string' ||
    (hasValue(config, 'icon') && typeof config.icon !== 'string') ||
    !Array.isArray(config['messages'])
  ) {
    return false;
  }

  if (!config['messages'].every((m) => isValidMessageConfig(m))) {
    return false;
  }

  return true;
}
